import React, { useState } from 'react';
import { FaLock, FaEye, FaEyeSlash, FaKey } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../utils/api';

// Get security settings from current organization
const current_organization = JSON.parse(sessionStorage.getItem('current_organization'));
const security_settings = current_organization?.security_settings || {};

const has_uppercase_requirement = security_settings.require_uppercase || false;
const has_lowercase_requirement = security_settings.require_lowercase || false;
const has_number_requirement = security_settings.require_numbers || false;
const has_special_char_requirement = security_settings.require_special_characters || false;
const has_min_length_requirement = security_settings.require_min_length || false;
const min_password_length = security_settings.min_password_length || 8;


const PasswordResetForm = ({ user, onPasswordChanged, isModal = false }) => {
  const { logout } = useAuth();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validatePassword = (password) => {
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (has_min_length_requirement && password.length < min_password_length) {
      return `Password must be at least ${min_password_length} characters long`;
    }
    if (has_uppercase_requirement && !hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (has_lowercase_requirement && !hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (has_number_requirement && !hasNumbers) {
      return 'Password must contain at least one number';
    }
    if (has_special_char_requirement && !hasSpecialChar) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      setIsLoading(false);
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      setError(passwordError);
      setIsLoading(false);
      return;
    }

    // Check if password is not a repeat of recent passwords
    try {
      const historyResponse = await apiService.post('/user_account_password_history/check', {
        new_password: formData.newPassword,
        user_account_id: user.id
      });
      console.log('Password history check response:', historyResponse);
      if (historyResponse.success) {
        console.log('Password history check response:', historyResponse.data);
        if (historyResponse.data.is_valid === false) {

          setError('This password has been used recently. Please choose a different password.');
          setIsLoading(false);
          return;
        }
      }
      else {
        console.log('Password history check response:', historyResponse);
        setError('Password history check failed');
        setIsLoading(false);
        return;
      }
    } catch (error) {
      console.error('Password history check error:', error);
      setError('Unable to verify password history. Please try again.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await apiService.post('/user_account_password/change', {
        current_password: formData.currentPassword,
        new_password: formData.newPassword,
        user_account_id: user.id
      });

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          onPasswordChanged();
        }, 2000);
      } else {
        setError(response.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Password change error:', error);
      setError(error.message || 'An error occurred while changing your password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  if (success) {
    const successContent = (
      <div className="text-center">
        <div className="text-success mb-4">
          <FaKey size={48} className="mb-3" />
          <h2 className="fw-bold text-success">Password Changed Successfully!</h2>
          <p className="text-muted">You can now continue using the application.</p>
        </div>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Redirecting...</span>
        </div>
      </div>
    );

    if (isModal) {
      return (
        <div className="p-4">
          {successContent}
        </div>
      );
    }

    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-4">
              <div className="card shadow-lg border-0">
                <div className="card-body p-5">
                  {successContent}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formContent = (
    <div>
      <div className="text-center mb-4">
        <FaKey size={48} className="text-warning mb-3" />
        <h2 className="fw-bold text-dark">{isModal ? 'Change Password' : 'Password Change Required'}</h2>
        <p className="text-muted">
          {isModal ? 'Enter your current password and choose a new one.' : 'For security reasons, you must change your password before continuing.'}
        </p>
        {user && (
          <p className="text-muted">
            <small>Logged in as: <strong>{user.user_name || user.name}</strong></small>
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="currentPassword" className="form-label fw-semibold">
            Current Password
          </label>
          <div className="input-group">
            <span className="input-group-text bg-light border-end-0">
              <FaLock className="text-muted" />
            </span>
            <input
              type={showPasswords.current ? "text" : "password"}
              className="form-control border-start-0 border-end-0 ps-0"
              id="currentPassword"
              name="currentPassword"
              placeholder="Enter your current password"
              value={formData.currentPassword}
              onChange={handleInputChange}
              required
              autoFocus
            />
            <button
              type="button"
              className="btn btn-outline-secondary border-start-0"
              onClick={() => togglePasswordVisibility('current')}
            >
              {showPasswords.current ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="newPassword" className="form-label fw-semibold">
            New Password
          </label>
          <div className="input-group">
            <span className="input-group-text bg-light border-end-0">
              <FaLock className="text-muted" />
            </span>
            <input
              type={showPasswords.new ? "text" : "password"}
              className="form-control border-start-0 border-end-0 ps-0"
              id="newPassword"
              name="newPassword"
              placeholder="Enter your new password"
              value={formData.newPassword}
              onChange={handleInputChange}
              required
            />
            <button
              type="button"
              className="btn btn-outline-secondary border-start-0"
              onClick={() => togglePasswordVisibility('new')}
            >
              {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="confirmPassword" className="form-label fw-semibold">
            Confirm New Password
          </label>
          <div className="input-group">
            <span className="input-group-text bg-light border-end-0">
              <FaLock className="text-muted" />
            </span>
            <input
              type={showPasswords.confirm ? "text" : "password"}
              className="form-control border-start-0 border-end-0 ps-0"
              id="confirmPassword"
              name="confirmPassword"
              placeholder="Confirm your new password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
            />
            <button
              type="button"
              className="btn btn-outline-secondary border-start-0"
              onClick={() => togglePasswordVisibility('confirm')}
            >
              {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        </div>

        {/* Password requirements */}
        {(has_min_length_requirement || has_uppercase_requirement || has_lowercase_requirement || has_number_requirement || has_special_char_requirement) && (
          <div className="mb-3">
            <small className="text-muted">
              <strong>Password Requirements:</strong>
              <ul className="mb-0 mt-1">
                {has_min_length_requirement && <li>At least {min_password_length} characters long</li>}
                {has_uppercase_requirement && <li>Contains at least one uppercase letter</li>}
                {has_lowercase_requirement && <li>Contains at least one lowercase letter</li>}
                {has_number_requirement && <li>Contains at least one number</li>}
                {has_special_char_requirement && <li>Contains at least one special character</li>}
              </ul>
            </small>
          </div>
        )}

        {error && (
          <div className="alert alert-danger d-flex align-items-center mb-3" role="alert">
            <div>
              <small>{error}</small>
            </div>
          </div>
        )}

        <div className="d-grid gap-2">
          <button
            type="submit"
            className="btn btn-primary py-2 fw-semibold"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Changing Password...
              </>
            ) : (
              <>
                <FaKey className="me-2" />
                Change Password
              </>
            )}
          </button>

          {!isModal && (
            <button
              type="button"
              className="btn btn-outline-secondary py-2"
              onClick={handleLogout}
              disabled={isLoading}
            >
              Sign Out Instead
            </button>
          )}
        </div>
      </form>

      {!isModal && (
        <div className="text-center mt-4">
          <small className="text-muted">
            Electronic Dealer Management System
          </small>
        </div>
      )}
    </div>
  );

  if (isModal) {
    return (
      <div className="p-4">
        {formContent}
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="card shadow-lg border-0">
              <div className="card-body p-5">
                {formContent}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetForm;